import { useState, useRef, useEffect } from "react";
import { Container, Row, Col, Button, Alert, Spinner, ButtonGroup, OverlayTrigger, Tooltip as BSTooltip, Form } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { MdFileUpload, MdAnalytics, MdExpandMore, MdExpandLess, MdTimeline, MdDownload } from "react-icons/md";
import DownloadDataModal from "../components/sessions/detail/DownloadDataModal";
import { requestWithAuthorization } from "../network/apiRequests";
import { MapContainer, TileLayer, Polyline, useMap, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

// Helper component: auto-fits the map view to the segment bounds on each render
function FitBounds({ positions }: { positions: [number, number][] }) {
  const map = useMap();
  if (positions.length > 0) {
    map.fitBounds(L.latLngBounds(positions), { padding: [32, 32] });
  }
  return null;
}

// Helper function: Maps a value from [min, max] to a color gradient (Red -> Yellow -> Green)
function getHeatmapColor(value: number, min: number = 0.8, max: number = 1.1) {
  // Clamp value
  const clamped = Math.max(min, Math.min(max, value));
  // Normalize to 0-1
  const ratio = (clamped - min) / (max - min);

  // HSL: 0 is Red (worst), 60 is Yellow (neutral/middle), 120 is Green (best)
  const hue = ratio * 120;
  return `hsl(${hue}, 100%, 50%)`;
}

interface MetricData {
  mean: number;
  values: number[];
}

interface SegmentTrajectory {
  start_idx: number;
  end_idx: number;
}

interface FullTrajectory {
  latitude: number[];
  longitude: number[];
}

interface TripData {
  GI: MetricData;
  TI: MetricData;
  heading: { mean: number };
  apparentWindSpeed: MetricData;
  gps_speed: MetricData;
  cDA?: MetricData;
  power_watts?: MetricData;
  f_wind?: MetricData;
  trajectory?: SegmentTrajectory;
}

interface AnalyticsData {
  [segmentId: string]: TripData[];
}

export default function AnalysisPage() {
  const location = useLocation();
  const { recordingId, name, startDateTime, endDateTime } = location.state || {};

  // --- Sensor Selection State ---
  const [sensorIds, setSensorIds] = useState<string[]>([]);
  const [selectedSensorId, setSelectedSensorId] = useState<string | null>(null);
  const [metadataLoading, setMetadataLoading] = useState<boolean>(false);
  const [metadataError, setMetadataError] = useState<string>("");
  const [selectedModel, setSelectedModel] = useState<string>("generic");

  // --- Analysis API State ---
  const [analysisLoading, setAnalysisLoading] = useState<boolean>(false);
  const [analysisError, setAnalysisError] = useState<string>("");
  const [analysisSuccess, setAnalysisSuccess] = useState<boolean>(false);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [trajectory, setTrajectory] = useState<FullTrajectory | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [selectedView, setSelectedView] = useState<{ segmentId: string; tripIndex: number } | null>(null);
  const [expandedSegments, setExpandedSegments] = useState<Record<string, boolean>>({});

  // --- GeoJSON Upload State ---
  const [file, setFile] = useState<File | null>(null);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [geoJsonData, setGeoJsonData] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- CAN Data (CSV) Upload State ---
  const [canFile, setCanFile] = useState<File | null>(null);
  const [canErrorMsg, setCanErrorMsg] = useState<string>("");
  const canFileInputRef = useRef<HTMLInputElement>(null);

  // --- Advanced Model Parameters ---
  const [vehicleMass, setVehicleMass] = useState<number>(1850);
  const [frontalArea, setFrontalArea] = useState<number>(2.46);
  const [rollingResistance, setRollingResistance] = useState<number>(0.01);
  const [drivetrainEfficiency, setDrivetrainEfficiency] = useState<number>(0.80);

  // --- Third Party Data State (for Cycling) ---
  const [thirdPartyExists, setThirdPartyExists] = useState<boolean | null>(null);
  const [checkingThirdParty, setCheckingThirdParty] = useState<boolean>(false);
  const [fitFile, setFitFile] = useState<File | null>(null);
  const [isUploadingFit, setIsUploadingFit] = useState<boolean>(false);
  const [fitErrorMsg, setFitErrorMsg] = useState<string>("");
  const fitFileInputRef = useRef<HTMLInputElement>(null);

  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [planLoading, setPlanLoading] = useState<boolean>(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // Fetch recording metadata on mount to get available sensor IDs
  useEffect(() => {
    if (!recordingId) return;

    const fetchMetadata = async () => {
      setMetadataLoading(true);
      setMetadataError("");
      try {
        const response = await requestWithAuthorization(
          `${process.env.REACT_APP_API_HOSTNAME}/recordings/${recordingId}`
        );
        if (response.status === 200) {
          const ids: string[] = response.data?.sensorId ?? [];
          setSensorIds(ids);
        } else {
          setMetadataError("Failed to load recording metadata.");
        }
      } catch (err) {
        setMetadataError("Failed to load recording metadata.");
      } finally {
        setMetadataLoading(false);
      }
    };

    fetchMetadata();
  }, [recordingId]);

  // Fetch user license/plan
  useEffect(() => {
    const fetchLicense = async () => {
      setPlanLoading(true);
      try {
        const response = await requestWithAuthorization(
          `${process.env.REACT_APP_API_HOSTNAME}/user/license`
        );
        if (response.status === 200) {
          setUserPlan(response.data?.plan_id ?? "free");
        }
      } catch (err) {
        console.error("Failed to fetch user license:", err);
        setUserPlan("free"); // Fallback to free on error
      } finally {
        setPlanLoading(false);
      }
    };

    fetchLicense();
  }, []);

  // Check for third-party data existence when Cycling model is selected
  useEffect(() => {
    if (selectedModel !== "cycling" || !recordingId) {
      setThirdPartyExists(null);
      return;
    }

    const checkThirdParty = async () => {
      setCheckingThirdParty(true);
      try {
        const response = await requestWithAuthorization(
          `${process.env.REACT_APP_API_HOSTNAME}/recordings/third-party-exists?recordingsId=${recordingId}`
        );
        if (response.status === 200) {
          setThirdPartyExists(response.data?.exists ?? false);
        }
      } catch (err) {
        console.error("Failed to check third-party data existence:", err);
      } finally {
        setCheckingThirdParty(false);
      }
    };

    checkThirdParty();
  }, [selectedModel, recordingId]);

  // Run the analysis API call
  const handleRunAnalysis = async () => {
    if (!recordingId || !selectedSensorId) return;

    setAnalysisLoading(true);
    setAnalysisError("");
    setAnalysisSuccess(false);

    const formData = new FormData();
    if (isValid && file) {
      formData.append("geojson", file);
    } else if (isValid && geoJsonData) {
      // Fallback if data exists but file doesn't (though current UI always sets file)
      formData.append("geojson", new Blob([JSON.stringify(geoJsonData)], { type: 'application/json' }), "segmentation.geojson");
    }

    if (canFile) {
      formData.append("can_data", canFile);
    }

    if (selectedModel === "automotive_advanced") {
      formData.append("model_parameters", JSON.stringify({
        m: vehicleMass,
        A: frontalArea,
        cr: rollingResistance,
        eta_ts: drivetrainEfficiency
      }));
    }

    try {
      const response = await requestWithAuthorization(
        `${process.env.REACT_APP_API_HOSTNAME}/recordings/${recordingId}/analysis?sensorId=${selectedSensorId}&model=${selectedModel}`,
        {},
        "post",
        formData
      );
      if (response.status === 200) {
        setAnalysisSuccess(true);
        if (response.data && response.data.analytics) {
          const newAnalytics = response.data.analytics;
          setAnalytics(newAnalytics);

          // Store full trajectory if available
          if (response.data.trajectory) {
            setTrajectory(response.data.trajectory);
          }

          // Select the first segment and first trip (index 0) by default
          const firstSegmentId = Object.keys(newAnalytics)[0];
          if (firstSegmentId) {
            const segmentTrips = newAnalytics[firstSegmentId];
            if (segmentTrips && segmentTrips.length > 0) {
              setSelectedView({ segmentId: firstSegmentId, tripIndex: 0 });
              setExpandedSegments({ [firstSegmentId]: true });
              setHoveredIndex(null);
            }
          }
        }
      } else {
        setAnalysisError("Analysis request failed. Please try again.");
      }
    } catch (err) {
      setAnalysisError("Analysis request failed. Please try again.");
    } finally {
      setAnalysisLoading(false);
    }
  };

  // --- GeoJSON Validation ---
  const validateGeoJson = (data: any): boolean => {
    if (!data || typeof data !== "object") return false;
    if (!Array.isArray(data.features)) return false;
    for (const feature of data.features) {
      if (!feature.geometry || typeof feature.geometry !== "object") return false;
      if (!feature.geometry.coordinates) return false;
    }
    return true;
  };

  const handleFitFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    setFitFile(selectedFile);
    setFitErrorMsg("");
  };

  const handleFitFileUpload = async () => {
    if (!fitFile || !recordingId) return;

    setIsUploadingFit(true);
    setFitErrorMsg("");

    const formData = new FormData();
    formData.append("file", fitFile);

    const url = `${process.env.REACT_APP_API_HOSTNAME}/recordings/${recordingId}/link?serviceName=fitupload&activityId=1`;
    try {
      const response = await requestWithAuthorization(
        url,
        {},
        "post",
        formData
      );
      if (response.status === 200) {
        setThirdPartyExists(true);
        setFitFile(null);
        alert("FIT file uploaded and synced successfully!");
      } else {
        setFitErrorMsg(`Failed to upload FIT file: ${response.data}`);
      }
    } catch (error) {
      setFitErrorMsg("Failed to upload FIT file. Please try again.");
      console.error("Error uploading FIT file:", error);
    } finally {
      setIsUploadingFit(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    setIsValid(null);
    setGeoJsonData(null);
    setErrorMsg("");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target?.result as string);
        const valid = validateGeoJson(json);
        setIsValid(valid);
        if (valid) {
          setGeoJsonData(json);
        } else {
          setErrorMsg("Invalid structure: must have 'features' array, and each feature must have 'geometry.coordinates'.");
        }
      } catch (err) {
        setIsValid(false);
        setErrorMsg("Failed to parse JSON file.");
      }
    };
    reader.readAsText(selectedFile);
  };

  const triggerFileSelect = () => fileInputRef.current?.click();

  const handleCanFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    setCanFile(selectedFile);
    setCanErrorMsg("");
  };

  const triggerCanFileSelect = () => canFileInputRef.current?.click();

  return (
    <Container
      fluid
      style={{ backgroundColor: "#091014", minHeight: "100vh", color: "#fff" }}
      className="p-4"
    >
      {/* Header */}
      <Row className="mb-5">
        <Col className="text-center">
          <h1 className="display-4 mb-3">Analysis</h1>
          <h3 className="text-muted mb-2">{name || "Unknown Recording"}</h3>
          {startDateTime && (
            <p className="detail-page-subheading">
              {new Date(startDateTime).toLocaleString()}
            </p>
          )}
        </Col>
      </Row>

      {/* Analytics Visualization Section */}
      {analysisSuccess && analytics && selectedView && analytics[selectedView.segmentId]?.[selectedView.tripIndex] && (
        <Row className="mb-5 justify-content-center">
          <Col lg={10} xl={9}>
            <div className="mb-4 d-flex align-items-center justify-content-between">
              <h3 className="h4 mb-0 d-flex align-items-center gap-3">
                <MdTimeline className="text-primary" size={28} />
                Session Insights - <span className="text-muted small">Run {selectedView.tripIndex + 1}</span>
              </h3>
            </div>

            <Row className="mb-4 g-4">
              <Col md={3}>
                <div
                  className="p-4 rounded-4 border border-white border-opacity-10 h-100"
                  style={{ background: "rgba(255,255,255,0.02)" }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted small fw-bold text-uppercase">Avg GI</span>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <BSTooltip id="gi-tooltip" style={{ maxWidth: '400px' }}>
                          <strong>GI (Gust Intensity):</strong> <br />
                          Indicates how much the current wind speed deviates from the typical wind level over the measurement period. <br />
                          The higher the value, the more exceptional or stronger the current gust is compared to the normal wind conditions. <br />
                          <br />• Low → wind corresponds to the usual level
                          <br />• High → pronounced gust or unusual increase in wind speed
                        </BSTooltip>
                      }
                    >
                      <div className="text-primary cursor-pointer d-flex align-items-center justify-content-center rounded-circle border border-primary border-opacity-25" style={{ width: 22, height: 22, fontSize: '0.7rem' }}>?</div>
                    </OverlayTrigger>
                  </div>
                  <div className="h2 mb-0 fw-bold text-primary">
                    {analytics[selectedView.segmentId][selectedView.tripIndex].GI.mean.toFixed(2)}%
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div
                  className="p-4 rounded-4 border border-white border-opacity-10 h-100"
                  style={{ background: "rgba(255,255,255,0.02)" }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted small fw-bold text-uppercase">Avg TI</span>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <BSTooltip id="ti-tooltip" style={{ maxWidth: '400px' }}>
                          <strong>TI (Turbulence Intensity):</strong> <br />
                          Describes the degree of wind speed fluctuation. <br />
                          A higher value indicates more turbulent or "unsteady" wind, while a lower value indicates more uniform wind flow. <br />
                          <br />• Low → steady wind
                          <br />• High → gusty, unstable wind
                        </BSTooltip>
                      }
                    >
                      <div className="text-info cursor-pointer d-flex align-items-center justify-content-center rounded-circle border border-info border-opacity-25" style={{ width: 22, height: 22, fontSize: '0.7rem' }}>?</div>
                    </OverlayTrigger>
                  </div>
                  <div className="h2 mb-0 fw-bold text-info">
                    {analytics[selectedView.segmentId][selectedView.tripIndex].TI.mean.toFixed(2)}%
                  </div>
                </div>
              </Col>
              {typeof analytics[selectedView.segmentId][selectedView.tripIndex].cDA?.mean === 'number' && (
                <Col md={3}>
                  <div
                    className="p-4 rounded-4 border border-white border-opacity-10 h-100"
                    style={{ background: "rgba(255,255,255,0.02)" }}
                  >
                    <span className="text-muted small d-block mb-2 fw-bold text-uppercase">Avg cDA</span>
                    <div className="h2 mb-0 fw-bold text-light">
                      {analytics[selectedView.segmentId][selectedView.tripIndex].cDA!.mean.toFixed(3)}
                    </div>
                  </div>
                </Col>
              )}
              {typeof analytics[selectedView.segmentId][selectedView.tripIndex].power_watts?.mean === 'number' && (
                <Col md={3}>
                  <div
                    className="p-4 rounded-4 border border-white border-opacity-10 h-100"
                    style={{ background: "rgba(255,255,255,0.02)" }}
                  >
                    <span className="text-muted small d-block mb-2 fw-bold text-uppercase">Avg Power</span>
                    <div className="h2 mb-0 fw-bold text-light">
                      {Math.round(analytics[selectedView.segmentId][selectedView.tripIndex].power_watts!.mean)}W
                    </div>
                  </div>
                </Col>
              )}
              {typeof analytics[selectedView.segmentId][selectedView.tripIndex].f_wind?.mean === 'number' && (
                <Col md={3}>
                  <div
                    className="p-4 rounded-4 border border-white border-opacity-10 h-100"
                    style={{ background: "rgba(255,255,255,0.02)" }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="text-muted small fw-bold text-uppercase">Avg f_wind</span>
                      <OverlayTrigger
                        placement="bottom"
                        overlay={
                          <BSTooltip id="fwind-tooltip" style={{ maxWidth: '400px' }}>
                            <strong>f_wind (Wind Influence Factor):</strong> <br />
                            Represents the overall impact of wind on your performance. <br />

                            <br />• <strong>1.0</strong> → Neutral impact
                            <br />• <strong>&lt; 1.0</strong> → Performance penalty (headwinds/unfavorable angles)
                            <br />• <strong>&gt; 1.0</strong> → Performance boost (tailwind assistance)
                          </BSTooltip>
                        }
                      >
                        <div className="text-success cursor-pointer d-flex align-items-center justify-content-center rounded-circle border border-success border-opacity-25" style={{ width: 22, height: 22, fontSize: '0.7rem' }}>?</div>
                      </OverlayTrigger>
                    </div>
                    <div className="h2 mb-0 fw-bold text-light">
                      {analytics[selectedView.segmentId][selectedView.tripIndex].f_wind!.mean.toFixed(2)}
                    </div>
                  </div>
                </Col>
              )}
            </Row>

            {(() => {
              const tripTraj = analytics[selectedView.segmentId][selectedView.tripIndex].trajectory;
              if (!trajectory || !tripTraj) return null;
              const allPoints: [number, number][] = trajectory.latitude.map(
                (lat, i) => [lat, trajectory.longitude[i]]
              );
              const segPoints: [number, number][] = trajectory.latitude
                .slice(tripTraj.start_idx, tripTraj.end_idx + 1)
                .map((lat, i) => [lat, trajectory.longitude[tripTraj.start_idx + i]]);
              const center: [number, number] = segPoints.length > 0
                ? segPoints[Math.floor(segPoints.length / 2)]
                : allPoints[Math.floor(allPoints.length / 2)];
              return (
                <div
                  className="mb-4 rounded-4 overflow-hidden border border-secondary border-opacity-25"
                  style={{ height: 350, position: "relative" }}
                >
                  {/* Legend overlay */}
                  <div
                    style={{
                      position: "absolute", bottom: 10, left: 10, zIndex: 1000,
                      background: "rgba(13,17,23,0.82)", borderRadius: 8,
                      padding: "6px 12px", backdropFilter: "blur(6px)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      fontSize: "0.72rem", color: "#8b949e",
                      display: "flex", flexDirection: "column", gap: 4
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ display: "inline-block", width: 18, height: 3, background: "#555", borderRadius: 2 }} />
                      Full Track
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{
                        display: "inline-block", width: 40, height: 6, borderRadius: 3,
                        background: "linear-gradient(to right, hsl(0,100%,50%), hsl(60,100%,50%), hsl(120,100%,50%))"
                      }} />
                      f_wind (0.8 - 1.1+)
                    </span>
                  </div>
                  <MapContainer
                    center={center}
                    zoom={15}
                    style={{ width: "100%", height: "100%" }}
                    scrollWheelZoom={true}
                    zoomControl={true}
                    dragging={true}
                    keyboard={true}
                    doubleClickZoom={true}
                    key={`${selectedView.segmentId}-${selectedView.tripIndex}`}
                  >
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                      attribution='&copy; <a href="https://carto.com" target="_blank">CARTO</a>'
                    />
                    <FitBounds positions={segPoints} />
                    <Polyline positions={allPoints} pathOptions={{ color: "#4b5263", weight: 3, opacity: 0.7 }} />

                    {/* Render segment as a series of colored heat-mapped polylines based on f_wind */}
                    {segPoints.map((point, i) => {
                      if (i === 0) return null; // Need 2 points for a line
                      const prevPoint = segPoints[i - 1];
                      // Default to blue if no f_wind data
                      let color = "#4f9eff";
                      const fWindData = analytics[selectedView.segmentId][selectedView.tripIndex].f_wind;
                      if (fWindData && fWindData.values && fWindData.values[i]) {
                        color = getHeatmapColor(fWindData.values[i]);
                      }

                      return (
                        <Polyline
                          key={`fwind-seg-${i}`}
                          positions={[prevPoint, point]}
                          pathOptions={{ color, weight: 6, opacity: 1, lineCap: "round", lineJoin: "round" }}
                        />
                      );
                    })}

                    {hoveredIndex !== null && trajectory && (
                      <CircleMarker
                        center={[
                          trajectory.latitude[tripTraj.start_idx + hoveredIndex],
                          trajectory.longitude[tripTraj.start_idx + hoveredIndex]
                        ]}
                        radius={7}
                        pathOptions={{
                          color: "#fff",
                          fillColor: (analytics[selectedView.segmentId][selectedView.tripIndex].f_wind && analytics[selectedView.segmentId][selectedView.tripIndex].f_wind?.values[hoveredIndex])
                            ? getHeatmapColor(analytics[selectedView.segmentId][selectedView.tripIndex].f_wind!.values[hoveredIndex])
                            : "#0d6efd",
                          fillOpacity: 1,
                          weight: 2
                        }}
                      />
                    )}
                  </MapContainer>
                </div>
              );
            })()}

            <div
              className="p-4 border border-primary border-opacity-10 rounded shadow-lg"
              style={{
                backgroundColor: "#0d1117",
                background: "linear-gradient(145deg, #0d1117 0%, #161b22 100%)",
              }}
            >
              <div style={{ width: "100%", height: 350 }}>
                <ResponsiveContainer>
                  <AreaChart
                    data={analytics[selectedView.segmentId][selectedView.tripIndex].GI.values.map((giValue: number, index: number) => ({
                      index,
                      GI: giValue,
                      TI: analytics[selectedView.segmentId][selectedView.tripIndex].TI.values[index],
                      apparentWindSpeed: analytics[selectedView.segmentId][selectedView.tripIndex].apparentWindSpeed.values[index],
                      gps_speed: analytics[selectedView.segmentId][selectedView.tripIndex].gps_speed.values[index],
                    }))}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    onMouseMove={(state) => {
                      if (state && state.activeTooltipIndex !== undefined) {
                        setHoveredIndex(state.activeTooltipIndex);
                      }
                    }}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <defs>
                      <linearGradient id="colorGI" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0d6efd" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#0d6efd" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorTI" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0dcaf0" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#0dcaf0" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorAWS" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ffc107" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#ffc107" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorGPS" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#fd7e14" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#fd7e14" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#30363d" />
                    <XAxis
                      dataKey="index"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#8b949e", fontSize: 10 }}
                      minTickGap={100}
                    />
                    <YAxis
                      yAxisId="left"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#8b949e", fontSize: 10 }}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#8b949e", fontSize: 10 }}
                      tickFormatter={(v) => `${v} m/s`}
                    />
                    <Tooltip
                      labelFormatter={() => ""}
                      contentStyle={{ backgroundColor: "#161b22", border: "1px solid #30363d", borderRadius: "8px" }}
                      itemStyle={{ fontSize: "12px" }}
                      formatter={(value: any, name: string) => {
                        const unit = (name === "AWS" || name === "GPS") ? "m/s" : "%";
                        return [`${value.toFixed(1)}${unit}`, `${name}:`];
                      }}
                    />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="TI"
                      stroke="#0dcaf0"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorTI)"
                      name="TI"
                      animationDuration={1000}
                    />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="GI"
                      stroke="#0d6efd"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorGI)"
                      name="GI"
                      animationDuration={1000}
                    />
                    <Area
                      yAxisId="right"
                      type="monotone"
                      dataKey="apparentWindSpeed"
                      stroke="#ffc107"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorAWS)"
                      name="AWS"
                      animationDuration={1000}
                    />
                    <Area
                      yAxisId="right"
                      type="monotone"
                      dataKey="gps_speed"
                      stroke="#fd7e14"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorGPS)"
                      name="GPS"
                      animationDuration={1000}
                    />
                    <ReferenceLine
                      yAxisId="left"
                      y={analytics[selectedView.segmentId][selectedView.tripIndex].GI.mean}
                      stroke="#0d6efd"
                      strokeDasharray="5 5"
                    />
                    <ReferenceLine
                      yAxisId="left"
                      y={analytics[selectedView.segmentId][selectedView.tripIndex].TI.mean}
                      stroke="#0dcaf0"
                      strokeDasharray="5 5"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* Segment Tables Section */}
      {analytics && (
        <Row className="mb-5 justify-content-center">
          <Col md={12} lg={10}>
            {Object.entries(analytics).map(([segmentId, segmentEntries]) => {
              const isExpanded = expandedSegments[segmentId];
              return (
                <div
                  key={segmentId}
                  className="mb-4 overflow-hidden rounded-4 border border-white border-opacity-10 shadow-sm"
                  style={{
                    backgroundColor: "#0d1117",
                    background: "rgba(13, 17, 23, 0.7)",
                    backdropFilter: "blur(12px)",
                    transition: "all 0.3s ease"
                  }}
                >
                  {/* Segment Header (Toggle) */}
                  <div
                    className="p-4 d-flex align-items-center justify-content-between"
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => setExpandedSegments(prev => ({ ...prev, [segmentId]: !prev[segmentId] }))}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-2 rounded bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center">
                        <span className="fw-bold px-2">{segmentId}</span>
                      </div>
                      <span className="text-muted small">
                        {Object.keys(segmentEntries).length} {Object.keys(segmentEntries).length === 1 ? 'Trip' : 'Trips'} Available
                      </span>
                    </div>
                    <div className="text-muted">
                      {isExpanded ? <MdExpandLess size={28} /> : <MdExpandMore size={28} />}
                    </div>
                  </div>

                  {/* Collapsible Content */}
                  <div
                    style={{
                      maxHeight: isExpanded ? "1000px" : "0",
                      opacity: isExpanded ? 1 : 0,
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      overflow: "hidden"
                    }}
                  >
                    <div className="px-4 pb-4">
                      <div className="table-responsive rounded-3 border border-secondary border-opacity-10">
                        <table className="table table-dark table-hover mb-0" style={{ backgroundColor: "transparent" }}>
                          <thead>
                            <tr style={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
                              <th className="border-0 text-muted small px-3 py-3">Run</th>
                              <th className="border-0 text-muted small px-3 py-3">Avg Gust (GI)</th>
                              <th className="border-0 text-muted small px-3 py-3">Avg Turb (TI)</th>
                              <th className="border-0 text-muted small px-3 py-3">Avg Heading</th>
                              <th className="border-0 text-muted small px-3 py-3">Avg Speed</th>
                              {segmentEntries.some(t => typeof t.cDA?.mean === 'number') && (
                                <th className="border-0 text-muted small px-3 py-3">Avg cDA</th>
                              )}
                              {segmentEntries.some(t => typeof t.power_watts?.mean === 'number') && (
                                <th className="border-0 text-muted small px-3 py-3">Avg Power</th>
                              )}
                              {segmentEntries.some(t => typeof t.f_wind?.mean === 'number') && (
                                <th className="border-0 text-muted small px-3 py-3">
                                  <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                      <BSTooltip id="header-fwind-tooltip">
                                        Wind Influence Factor: Impact of wind on performance (neutral = 1.0, penalty &lt; 1.0, boost &gt; 1.0)
                                      </BSTooltip>
                                    }
                                  >
                                    <span style={{ cursor: 'help' }}>Avg f_wind</span>
                                  </OverlayTrigger>
                                </th>
                              )}
                              <th className="border-0 text-muted small px-3 py-3 text-end"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {segmentEntries.map((trip, tripIndex) => {
                              const isActive = selectedView?.tripIndex === tripIndex && selectedView?.segmentId === segmentId;
                              return (
                                <tr
                                  key={tripIndex}
                                  className={isActive ? "table-active" : ""}
                                  style={{ transition: "background-color 0.2s ease" }}
                                >
                                  <td className="align-middle border-0 px-3 py-3 border-bottom border-white border-opacity-5">
                                    <span className="fw-bold" style={{ color: "#8b949e" }}>{tripIndex + 1}</span>
                                  </td>
                                  <td className="align-middle border-0 px-3 py-3 border-bottom border-white border-opacity-5">
                                    <span className="text-primary fw-medium">{trip.GI.mean.toFixed(2)}%</span>
                                  </td>
                                  <td className="align-middle border-0 px-3 py-3 border-bottom border-white border-opacity-5">
                                    <span className="text-info fw-medium">{trip.TI.mean.toFixed(2)}%</span>
                                  </td>
                                  <td className="align-middle border-0 px-3 py-3 border-bottom border-white border-opacity-5">
                                    <span className="text-warning fw-medium">{trip.heading.mean.toFixed(1)}°</span>
                                  </td>
                                  <td className="align-middle border-0 px-3 py-3 border-bottom border-white border-opacity-5">
                                    <span className="text-secondary fw-medium">{trip.gps_speed.mean.toFixed(1)} m/s</span>
                                  </td>
                                  {segmentEntries.some(t => typeof t.cDA?.mean === 'number') && (
                                    <td className="align-middle border-0 px-3 py-3 border-bottom border-white border-opacity-5">
                                      {typeof trip.cDA?.mean === 'number' ? (
                                        <span className="text-light fw-medium">{trip.cDA.mean.toFixed(3)}</span>
                                      ) : (
                                        <span className="text-muted small">—</span>
                                      )}
                                    </td>
                                  )}
                                  {segmentEntries.some(t => typeof t.power_watts?.mean === 'number') && (
                                    <td className="align-middle border-0 px-3 py-3 border-bottom border-white border-opacity-5">
                                      {typeof trip.power_watts?.mean === 'number' ? (
                                        <span className="text-light fw-medium">{Math.round(trip.power_watts.mean)}W</span>
                                      ) : (
                                        <span className="text-muted small">—</span>
                                      )}
                                    </td>
                                  )}
                                  {segmentEntries.some(t => typeof t.f_wind?.mean === 'number') && (
                                    <td className="align-middle border-0 px-3 py-3 border-bottom border-white border-opacity-5">
                                      {typeof trip.f_wind?.mean === 'number' ? (
                                        <span className="text-light fw-medium">{trip.f_wind.mean.toFixed(2)}</span>
                                      ) : (
                                        <span className="text-muted small">—</span>
                                      )}
                                    </td>
                                  )}
                                  <td className="align-middle border-0 px-3 py-3 text-end border-bottom border-white border-opacity-5">
                                    <Button
                                      variant={isActive ? "primary" : "outline-primary"}
                                      size="sm"
                                      className="px-4 rounded-pill"
                                      style={{
                                        fontSize: "0.75rem",
                                        fontWeight: "600",
                                        boxShadow: isActive ? "0 0 15px rgba(13, 110, 253, 0.3)" : "none"
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent folding toggle
                                        setSelectedView({ segmentId, tripIndex });
                                        window.scrollTo({ top: 150, behavior: 'smooth' });
                                      }}
                                    >
                                      {isActive ? "ACTIVE" : "VIEW"}
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </Col>
        </Row>
      )}

      {/* Centerpiece: Sensor Selection + Run Analysis */}
      <Row className="mb-5 justify-content-center">
        <Col md={8} lg={6} className="text-center">
          <div className="p-5 border border-primary border-opacity-25 rounded shadow-lg" style={{ backgroundColor: "#0d1117" }}>
            <h2 className="mb-4">Ready to start?</h2>
            <p className="text-muted mb-4 px-3">
              Process your recording data to extract detailed performance metrics and segment-based analysis.
            </p>

            {/* Sensor selection */}
            {!recordingId ? (
              <Alert variant="warning" className="text-start small">
                No recording selected. Please open Analysis from a session view.
              </Alert>
            ) : metadataLoading ? (
              <div className="d-flex justify-content-center align-items-center gap-2 mb-4 text-muted">
                <Spinner animation="border" size="sm" />
                <span>Loading sensors…</span>
              </div>
            ) : metadataError ? (
              <Alert variant="danger" className="text-start small mb-4">{metadataError}</Alert>
            ) : sensorIds.length > 0 ? (
              <div className="mb-4">
                <p className="text-muted small mb-2">Select a sensor to analyse:</p>
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  {sensorIds.map((id) => (
                    <Button
                      key={id}
                      variant={selectedSensorId === id ? "primary" : "outline-secondary"}
                      onClick={() => setSelectedSensorId(id)}
                      className="px-3 py-2"
                      style={{ fontSize: "0.9rem", minWidth: "120px" }}
                    >
                      {id}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <Alert variant="secondary" className="text-start small mb-4">
                No sensors found for this recording.
              </Alert>
            )}

            {/* Status indicators */}
            {analysisError && (
              <Alert variant="danger" className="text-start small mb-3">{analysisError}</Alert>
            )}

            {/* Model Selection Dropdown */}
            <div className="mb-4 text-start mx-auto" style={{ maxWidth: "300px" }}>
              <Form.Group controlId="modelSelect">
                <Form.Label className="text-muted small mb-2 fw-bold text-uppercase">Analysis Model</Form.Label>
                <Form.Select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="bg-dark text-white border-secondary"
                  style={{
                    backgroundColor: "#161b22",
                    borderColor: "rgba(255,255,255,0.1)",
                    color: "#fff",
                    boxShadow: "none"
                  }}
                >
                  <option value="generic" disabled={userPlan === "free"}>{userPlan === "free" ? "Generic 🔒" : "Generic"}</option>
                  <option value="cycling" disabled={userPlan === "free"}>{userPlan === "free" ? "Cycling 🔒" : "Cycling"}</option>
                  <option value="automotive_simple" disabled={userPlan === "free"}>{userPlan === "free" ? "Automotive simple 🔒" : "Automotive simple"}</option>
                  <option value="automotive_advanced" disabled={userPlan === "free"}>{userPlan === "free" ? "Automotive advanced 🔒" : "Automotive advanced"}</option>
                </Form.Select>
              </Form.Group>
            </div>

            {userPlan === "free" && (
              <div className="mb-4 p-3 border border-warning border-opacity-25 rounded bg-warning bg-opacity-10 text-warning small text-center mx-auto" style={{ maxWidth: "400px" }}>
                <strong>Analysis Restricted:</strong> You are on the Free plan. Detailed performance analysis and advanced models are only available for <strong>Pro</strong> users.
              </div>
            )}

            <div className="d-flex justify-content-center gap-3">
              <Button
                variant="primary"
                className="px-5 py-2 fw-bold"
                onClick={handleRunAnalysis} // Assuming handleRunAnalysis is the correct function
                disabled={!selectedSensorId || analysisLoading || userPlan === "free"} // Assuming analysisLoading is the correct state
              >
                {planLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : analysisLoading ? ( // Assuming analysisLoading is the correct state
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Running…
                  </>
                ) : (
                  "Run analysis"
                )}
              </Button>
              <Button
                variant="outline-primary"
                className="px-4 py-2"
                onClick={() => setShowDownloadModal(true)}
              >
                <MdDownload className="me-2" />
                Download Data
              </Button>
            </div>
          </div>
        </Col>
      </Row>



      {/* Optional File Uploads */}
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          <hr className="border-secondary mb-5" />
          <Row className="g-4 justify-content-center">
            {/* FIT File Upload (for Cycling) */}
            {selectedModel === "cycling" && (thirdPartyExists === false || checkingThirdParty) && (
              <Col md={6}>
                <div className="p-4 border border-secondary rounded h-100" style={{ backgroundColor: "#161b22" }}>
                  <h5 className="mb-3 d-flex align-items-center gap-2">
                    <MdFileUpload size={18} className="text-primary" />
                    FIT File Upload & Sync
                  </h5>

                  {checkingThirdParty ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" className="me-2 text-primary" />
                      <span className="text-muted small">Checking for existing data...</span>
                    </div>
                  ) : (
                    <>
                      <p className="text-muted small mb-4">
                        To include power and cadence data in your cycling analysis, please upload the original .fit file from your bike computer.
                        <br /><br />
                        This will sync the performance data with your recording for detailed analysis.
                      </p>

                      <input
                        type="file"
                        accept=".fit"
                        onChange={handleFitFileChange}
                        ref={fitFileInputRef}
                        style={{ display: "none" }}
                      />

                      <div className="d-flex justify-content-center gap-2">
                        <Button
                          variant="outline-light"
                          size="sm"
                          onClick={() => fitFileInputRef.current?.click()}
                          className="mb-3"
                        >
                          Select FIT File
                        </Button>
                        {fitFile && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleFitFileUpload}
                            className="mb-3"
                            disabled={isUploadingFit}
                          >
                            {isUploadingFit ? <Spinner animation="border" size="sm" /> : "Upload & Sync"}
                          </Button>
                        )}
                      </div>

                      {fitFile && (
                        <div className="mt-2 pt-2 border-top border-secondary">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <p className="mb-1 small"><strong>File:</strong> {fitFile.name}</p>
                              <p className="mb-0 small">
                                <strong>Status:</strong> <span className="text-success">Ready to upload</span>
                              </p>
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                setFitFile(null);
                                if (fitFileInputRef.current) fitFileInputRef.current.value = "";
                              }}
                              style={{ fontSize: "0.72rem", padding: "2px 8px" }}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      )}
                      {fitErrorMsg && (
                        <Alert variant="danger" className="mt-2 py-1 px-2 mb-0" style={{ fontSize: "0.75rem" }}>
                          {fitErrorMsg}
                        </Alert>
                      )}
                    </>
                  )}
                </div>
              </Col>
            )}

            {/* GeoJSON Upload */}
            <Col md={6}>
              <div className="p-4 border border-secondary rounded h-100" style={{ backgroundColor: "#161b22" }}>
                <h5 className="mb-3 d-flex align-items-center gap-2">
                  <MdFileUpload size={18} className="text-primary" />
                  GPS Geofence segments
                </h5>
                <p className="text-muted small mb-3">
                  Upload a .geojson file from <a href="https://geojson.io" target="_blank" rel="noopener noreferrer">geojson.io</a> to define specific segments for this analysis.
                  <br />
                  If no geojson file is provided, the analysis will be performed on the entire recording.
                  <br />
                  <br />
                  <strong><i>Tip: For each segment, you can add the property "name" to assign a custom segment name.</i></strong>
                </p>

                <input
                  type="file"
                  accept=".json,.geojson"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  style={{ display: "none" }}
                />

                <div className="d-flex justify-content-center">
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={triggerFileSelect}
                    className="mb-3"
                  >
                    Select GeoJSON
                  </Button>
                </div>

                {file && (
                  <div className="mt-2 pt-2 border-top border-secondary">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <p className="mb-1 small"><strong>File:</strong> {file.name}</p>
                        <p className="mb-0 small">
                          <strong>Status:</strong>{" "}
                          {isValid === null ? (
                            <span className="text-warning">Validating...</span>
                          ) : isValid ? (
                            <span className="text-success">Ready</span>
                          ) : (
                            <span className="text-danger">Invalid format</span>
                          )}
                        </p>
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          setFile(null);
                          setGeoJsonData(null);
                          setIsValid(null);
                          setErrorMsg("");
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                        style={{ fontSize: "0.72rem", padding: "2px 8px" }}
                      >
                        Remove
                      </Button>
                    </div>
                    {errorMsg && (
                      <Alert variant="danger" className="mt-2 py-1 px-2 mb-0" style={{ fontSize: "0.75rem" }}>
                        {errorMsg}
                      </Alert>
                    )}
                  </div>
                )}
              </div>
            </Col>

            {/* Vehicle Data (CSV) Upload - Only for automotive models */}
            {selectedModel.startsWith("automotive") && (
              <Col md={6}>
                <div className="p-4 border border-secondary rounded h-100" style={{ backgroundColor: "#161b22" }}>
                  <h5 className="mb-3 d-flex align-items-center gap-2">
                    <MdAnalytics size={18} className="text-primary" />
                    Vehicle Data (CSV)
                  </h5>
                  <p className="text-muted small mb-3">
                    Upload a .csv file containing vehicle data (e.g. CAN data) to include in the automotive analysis.
                    <br /><br />
                    The file must contain the columns: <strong><i>timestamp</i></strong>, <strong><i>vehicle_torque</i></strong> and <strong><i>vehicle_rpm</i></strong> for synchronization and analysis.
                  </p>

                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCanFileChange}
                    ref={canFileInputRef}
                    style={{ display: "none" }}
                  />

                  <div className="d-flex justify-content-center">
                    <Button
                      variant="outline-light"
                      size="sm"
                      onClick={triggerCanFileSelect}
                      className="mb-3"
                    >
                      Select CSV File
                    </Button>
                  </div>

                  {canFile && (
                    <div className="mt-2 pt-2 border-top border-secondary">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <p className="mb-1 small"><strong>File:</strong> {canFile.name}</p>
                          <p className="mb-0 small">
                            <strong>Status:</strong> <span className="text-success">Ready</span>
                          </p>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setCanFile(null);
                            setCanErrorMsg("");
                            if (canFileInputRef.current) canFileInputRef.current.value = "";
                          }}
                          style={{ fontSize: "0.72rem", padding: "2px 8px" }}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  )}
                  {canErrorMsg && (
                    <Alert variant="danger" className="mt-2 py-1 px-2 mb-0" style={{ fontSize: "0.75rem" }}>
                      {canErrorMsg}
                    </Alert>
                  )}

                  {/* Advanced Parameters - Only for automotive_advanced */}
                  {selectedModel === "automotive_advanced" && (
                    <div className="mt-4 pt-3 border-top border-secondary">
                      <h6 className="text-muted small fw-bold text-uppercase mb-3">Model Parameters</h6>
                      <Row className="g-3">
                        <Col xs={6}>
                          <Form.Group>
                            <Form.Label className="text-muted small mb-1">Vehicle Mass (kg)</Form.Label>
                            <Form.Control
                              type="number"
                              value={vehicleMass}
                              onChange={(e) => setVehicleMass(parseFloat(e.target.value) || 0)}
                              className="bg-dark text-white border-secondary form-control-sm"
                              placeholder="1850"
                              style={{ backgroundColor: "#0d1117", borderColor: "rgba(255,255,255,0.1)" }}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={6}>
                          <Form.Group>
                            <Form.Label className="text-muted small mb-1">Frontal Area (m²)</Form.Label>
                            <Form.Control
                              type="number"
                              step="0.01"
                              value={frontalArea}
                              onChange={(e) => setFrontalArea(parseFloat(e.target.value) || 0)}
                              className="bg-dark text-white border-secondary form-control-sm"
                              placeholder="2.46"
                              style={{ backgroundColor: "#0d1117", borderColor: "rgba(255,255,255,0.1)" }}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={6}>
                          <Form.Group>
                            <Form.Label className="text-muted small mb-1">Rolling Resistance</Form.Label>
                            <Form.Control
                              type="number"
                              step="0.001"
                              value={rollingResistance}
                              onChange={(e) => setRollingResistance(parseFloat(e.target.value) || 0)}
                              className="bg-dark text-white border-secondary form-control-sm"
                              placeholder="0.01"
                              style={{ backgroundColor: "#0d1117", borderColor: "rgba(255,255,255,0.1)" }}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={6}>
                          <Form.Group>
                            <Form.Label className="text-muted small mb-1">Drivetrain Efficiency</Form.Label>
                            <Form.Control
                              type="number"
                              step="0.01"
                              value={drivetrainEfficiency}
                              onChange={(e) => setDrivetrainEfficiency(parseFloat(e.target.value) || 0)}
                              className="bg-dark text-white border-secondary form-control-sm"
                              placeholder="0.80"
                              style={{ backgroundColor: "#0d1117", borderColor: "rgba(255,255,255,0.1)" }}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  )}
                </div>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
      <DownloadDataModal
        show={showDownloadModal}
        onHide={() => setShowDownloadModal(false)}
        recordingId={recordingId || ""}
        recordingName={name || "Recording"}
        sensorIds={sensorIds}
        startTime={startDateTime || ""}
        endTime={endDateTime || ""}
      />
    </Container>
  );
}
